import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import {
  createPromoCode,
  listPromoCodes,
  deactivatePromoCode,
  deletePromoCode,
  checkAdminStatusFn,
} from "../lib/subscription";
import type { PromoCode, PromoCodeType, UsageFeature } from "../lib/userStats";

const PROMO_CODE_TYPES: { id: PromoCodeType; label: string; description: string }[] = [
  { id: "premium", label: "Premium Access", description: "Full premium subscription" },
  { id: "trial", label: "Trial Period", description: "Time-limited premium access" },
  { id: "bonus", label: "Bonus Usage", description: "Extra free tier usage" },
];

const USAGE_FEATURES: { id: UsageFeature; label: string }[] = [
  { id: "chat", label: "Chat Questions" },
  { id: "mealBuilder", label: "Meal Builder" },
  { id: "mealPlanner", label: "Meal Planner" },
  { id: "recipeTransformer", label: "Recipe Transformer" },
  { id: "menuDecoder", label: "Menu Decoder" },
  { id: "groceryOptimizer", label: "Grocery Optimizer" },
];

export default function AdminPanel() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "",
    type: "trial" as PromoCodeType,
    durationDays: 7,
    maxRedemptions: 0,
    description: "",
    bonusUsage: {} as Partial<Record<UsageFeature, number>>,
  });
  const [creating, setCreating] = useState(false);

  // Check admin status
  useEffect(() => {
    async function checkAdmin() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const result = await checkAdminStatusFn();
        setIsAdmin(result.isAdmin);
      } catch (err) {
        console.error("Failed to check admin status:", err);
      } finally {
        setLoading(false);
      }
    }

    checkAdmin();
  }, [user]);

  // Load promo codes
  useEffect(() => {
    if (isAdmin) {
      loadPromoCodes();
    }
  }, [isAdmin]);

  const loadPromoCodes = async () => {
    setCodesLoading(true);
    try {
      const codes = await listPromoCodes();
      setPromoCodes(codes);
    } catch (err) {
      console.error("Failed to load promo codes:", err);
      setError("Failed to load promo codes");
    } finally {
      setCodesLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.code.trim()) return;

    setCreating(true);
    setError(null);

    try {
      const codeData: Partial<PromoCode> = {
        code: newCode.code.toUpperCase().trim(),
        type: newCode.type,
        description: newCode.description || undefined,
        maxRedemptions: newCode.maxRedemptions > 0 ? newCode.maxRedemptions : undefined,
      };

      if (newCode.type === "trial" && newCode.durationDays > 0) {
        codeData.durationDays = newCode.durationDays;
      }

      if (newCode.type === "bonus") {
        const bonusWithValues = Object.entries(newCode.bonusUsage)
          .filter(([, val]) => val && val > 0)
          .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
        if (Object.keys(bonusWithValues).length > 0) {
          codeData.bonusUsage = bonusWithValues;
        }
      }

      await createPromoCode(codeData);
      setShowCreateForm(false);
      setNewCode({
        code: "",
        type: "trial",
        durationDays: 7,
        maxRedemptions: 0,
        description: "",
        bonusUsage: {},
      });
      await loadPromoCodes();
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || "Failed to create promo code");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (code: string) => {
    if (!confirm(`Deactivate promo code "${code}"?`)) return;

    try {
      await deactivatePromoCode(code);
      await loadPromoCodes();
    } catch (err) {
      console.error("Failed to deactivate code:", err);
      setError("Failed to deactivate code");
    }
  };

  const handleDelete = async (code: string) => {
    if (!confirm(`Permanently delete promo code "${code}"? This cannot be undone.`)) return;

    try {
      await deletePromoCode(code);
      await loadPromoCodes();
    } catch (err) {
      console.error("Failed to delete code:", err);
      setError("Failed to delete code");
    }
  };

  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode((prev) => ({ ...prev, code }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <svg className="w-10 h-10 text-muted mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
        <h2 className="text-lg font-semibold text-text mb-1">Access Denied</h2>
        <p className="text-sm text-muted">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Admin</div>
        <p className="text-sm text-muted">Manage promo codes and subscriptions.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-gi-high/20 bg-gi-high-bg rounded-md">
          <p className="text-sm text-gi-high font-[family-name:--font-ui]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-xs text-gi-high/70 hover:underline mt-1 font-[family-name:--font-ui]"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Promo Codes Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-text font-[family-name:--font-ui] font-medium">Promo Codes</p>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-3 py-1.5 bg-accent text-white text-sm rounded-md font-[family-name:--font-ui] font-medium hover:opacity-85 transition-opacity"
          >
            {showCreateForm ? "Cancel" : "Create Code"}
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="mb-6 p-4 border border-border rounded-md">
            <p className="text-sm text-text font-[family-name:--font-ui] font-medium mb-4">New Promo Code</p>

            <div className="space-y-4">
              {/* Code Input */}
              <div>
                <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCode.code}
                    onChange={(e) => setNewCode((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="SUMMER2024"
                    className="flex-1 px-3 py-2 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent uppercase bg-bg text-text"
                  />
                  <button
                    type="button"
                    onClick={generateRandomCode}
                    className="px-3 py-2 border border-border text-text text-sm rounded-md font-[family-name:--font-ui] hover:bg-black/5 transition-colors"
                  >
                    Random
                  </button>
                </div>
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">Type</label>
                <div className="flex flex-wrap gap-2">
                  {PROMO_CODE_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setNewCode((prev) => ({ ...prev, type: type.id }))}
                      className={`px-3 py-2 rounded-md text-sm font-[family-name:--font-ui] font-medium transition-colors ${
                        newCode.type === type.id
                          ? "bg-accent text-white"
                          : "border border-border text-text hover:bg-black/5"
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted font-[family-name:--font-ui] mt-1">
                  {PROMO_CODE_TYPES.find((t) => t.id === newCode.type)?.description}
                </p>
              </div>

              {/* Duration (for trial) */}
              {newCode.type === "trial" && (
                <div>
                  <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Trial Duration (days)</label>
                  <input
                    type="number"
                    value={newCode.durationDays}
                    onChange={(e) => setNewCode((prev) => ({ ...prev, durationDays: parseInt(e.target.value) || 0 }))}
                    min="1"
                    max="365"
                    className="w-32 px-3 py-2 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent bg-bg text-text"
                  />
                </div>
              )}

              {/* Bonus Usage (for bonus type) */}
              {newCode.type === "bonus" && (
                <div>
                  <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">Bonus Usage Amounts</label>
                  <div className="grid grid-cols-2 gap-3">
                    {USAGE_FEATURES.map((feature) => (
                      <div key={feature.id} className="flex items-center gap-2">
                        <span className="text-sm text-muted font-[family-name:--font-ui] flex-1">{feature.label}</span>
                        <input
                          type="number"
                          value={newCode.bonusUsage[feature.id] || ""}
                          onChange={(e) =>
                            setNewCode((prev) => ({
                              ...prev,
                              bonusUsage: { ...prev.bonusUsage, [feature.id]: parseInt(e.target.value) || 0 },
                            }))
                          }
                          placeholder="0"
                          min="0"
                          className="w-20 px-2 py-1 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent bg-bg text-text"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Max Redemptions */}
              <div>
                <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Max Redemptions (0 = unlimited)</label>
                <input
                  type="number"
                  value={newCode.maxRedemptions}
                  onChange={(e) => setNewCode((prev) => ({ ...prev, maxRedemptions: parseInt(e.target.value) || 0 }))}
                  min="0"
                  className="w-32 px-3 py-2 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent bg-bg text-text"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Description (internal)</label>
                <input
                  type="text"
                  value={newCode.description}
                  onChange={(e) => setNewCode((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Summer promo for newsletter subscribers"
                  className="w-full px-3 py-2 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent bg-bg text-text"
                />
              </div>

              {/* Create Button */}
              <button
                onClick={handleCreateCode}
                disabled={!newCode.code.trim() || creating}
                className="w-full py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {creating ? "Creating..." : "Create Promo Code"}
              </button>
            </div>
          </div>
        )}

        {/* Codes List */}
        {codesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="spinner"></div>
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted font-[family-name:--font-ui]">No promo codes yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {promoCodes.map((code) => (
              <div
                key={code.code}
                className={`p-4 rounded-md border ${
                  code.isActive ? "border-border" : "border-border opacity-60"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-[family-name:--font-ui] font-bold text-text">{code.code}</span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-md font-[family-name:--font-ui] font-medium ${
                          code.type === "premium"
                            ? "bg-accent/10 text-accent"
                            : code.type === "trial"
                            ? "bg-gi-medium-bg text-gi-medium"
                            : "text-muted border border-border"
                        }`}
                      >
                        {code.type}
                      </span>
                      {!code.isActive && (
                        <span className="px-2 py-0.5 text-xs rounded-md font-[family-name:--font-ui] bg-gi-high-bg text-gi-high">Inactive</span>
                      )}
                    </div>
                    {code.description && <p className="text-sm text-muted mt-1">{code.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted font-[family-name:--font-ui]">
                      {code.type === "trial" && code.durationDays && <span>{code.durationDays} day trial</span>}
                      {code.maxRedemptions ? (
                        <span>
                          {code.currentRedemptions}/{code.maxRedemptions} redeemed
                        </span>
                      ) : (
                        <span>{code.currentRedemptions} redeemed (unlimited)</span>
                      )}
                      {code.expiresAt && <span>Expires: {new Date(code.expiresAt).toLocaleDateString()}</span>}
                      <span>Created: {new Date(code.createdAt).toLocaleDateString()}</span>
                    </div>
                    {code.type === "bonus" && code.bonusUsage && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(code.bonusUsage).map(([feature, amount]) => (
                          <span key={feature} className="px-2 py-0.5 text-xs rounded-md font-[family-name:--font-ui] border border-border text-muted">
                            +{amount} {feature}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {code.isActive && (
                      <button
                        onClick={() => handleDeactivate(code.code)}
                        className="px-3 py-1 text-xs font-[family-name:--font-ui] font-medium bg-gi-medium-bg text-gi-medium rounded-md hover:opacity-85 transition-opacity"
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(code.code)}
                      className="px-3 py-1 text-xs font-[family-name:--font-ui] font-medium bg-gi-high-bg text-gi-high rounded-md hover:opacity-85 transition-opacity"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="flex items-center gap-6 pt-6 border-t border-border-light">
        <div>
          <div className="text-xl font-semibold text-text font-[family-name:--font-ui]">{promoCodes.filter((c) => c.isActive).length}</div>
          <div className="text-xs text-muted font-[family-name:--font-ui]">Active Codes</div>
        </div>
        <div className="w-px h-8 bg-border-light" />
        <div>
          <div className="text-xl font-semibold text-text font-[family-name:--font-ui]">
            {promoCodes.reduce((sum, c) => sum + c.currentRedemptions, 0)}
          </div>
          <div className="text-xs text-muted font-[family-name:--font-ui]">Total Redemptions</div>
        </div>
        <div className="w-px h-8 bg-border-light" />
        <div>
          <div className="text-xl font-semibold text-text font-[family-name:--font-ui]">
            {promoCodes.filter((c) => c.type === "premium" && c.isActive).length}
          </div>
          <div className="text-xs text-muted font-[family-name:--font-ui]">Premium Codes</div>
        </div>
      </div>
    </div>
  );
}
